/**
 * Production Forecast API
 * Volume Forecast and EVI (Energy Community)
 */

import { apiClient } from "./client";
import type { EVIProfile, Granularity, VolumeForecastResponse } from "./types";

// ============================================================================
// VOLUME FORECAST (Section E)
// ============================================================================

/**
 * Get production/consumption forecast (d+1 or multi-day)
 * Note: Consumption is always 0 for now
 *
 * @param params.days - Number of days to forecast (1-14, default: 1)
 * @param params.granularity - PT60 for hourly (24 points/day) or PT15 for quarter-hourly (96 points/day)
 */
export async function getVolumeForecast(params?: {
  plantId?: number;
  targetDate?: string;
  days?: number;
  granularity?: Granularity;
  excludeMaintenance?: boolean;
}): Promise<VolumeForecastResponse> {
  const searchParams = new URLSearchParams();
  if (params?.plantId) searchParams.append("plant_id", params.plantId.toString());
  if (params?.targetDate) searchParams.append("target_date", params.targetDate);
  if (params?.days) searchParams.append("days", params.days.toString());
  if (params?.excludeMaintenance) searchParams.append("exclude_maintenance", "true");

  const query = searchParams.toString();
  // Use PT15 endpoint for quarter-hourly data
  const endpoint = params?.granularity === "PT15"
    ? `/api/v1/forecast/volume-pt15${query ? `?${query}` : ""}`
    : `/api/v1/forecast/volume${query ? `?${query}` : ""}`;

  return apiClient.get<VolumeForecastResponse>(endpoint);
}

/**
 * Get forecast summary with % change
 */
export async function getForecastSummary(params?: {
  plantId?: number;
}): Promise<{
  message: string;
}> {
  const searchParams = new URLSearchParams();
  if (params?.plantId) searchParams.append("plant_id", params.plantId.toString());

  const query = searchParams.toString();
  return apiClient.get<{ message: string }>(
    `/api/v1/forecast/summary${query ? `?${query}` : ""}`
  );
}

/**
 * Download forecast as CSV
 *
 * @param params.days - Number of days to include (1-14, default: 1)
 */
export async function downloadForecastCSV(params?: {
  plantId?: number;
  targetDate?: string;
  days?: number;
}): Promise<Blob> {
  const searchParams = new URLSearchParams();
  if (params?.plantId) searchParams.append("plant_id", params.plantId.toString());
  if (params?.targetDate) searchParams.append("target_date", params.targetDate);
  if (params?.days) searchParams.append("days", params.days.toString());

  const query = searchParams.toString();
  const url = `/api/v1/forecast/download${query ? `?${query}` : ""}`;

  // Use fetch directly for blob download
  const token = localStorage.getItem("vpp_auth_token");
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001"}${url}`,
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to download forecast");
  }

  return response.blob();
}

/**
 * Helper to trigger forecast CSV download in browser
 *
 * @param params.days - Number of days to include (1-14, default: 1)
 */
export async function triggerForecastDownload(params?: {
  plantId?: number;
  targetDate?: string;
  days?: number;
}): Promise<void> {
  const blob = await downloadForecastCSV(params);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const daysStr = params?.days && params.days > 1 ? `_${params.days}d` : "";
  a.download = `forecast_${params?.targetDate || "latest"}${daysStr}.csv`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// ============================================================================
// EVI - ENERGY COMMUNITY (Section E2)
// Uses historical EVI data from all community producers
// ============================================================================

import type {
  EVITypicalDayResponse,
  EVICommunityStats,
  EVIAutonomyStats,
  EVITypicalConsumptionProfile,
  EVIProductionZones,
  EVICombinedTypicalDay,
} from "./types";

/**
 * Get energy community typical day profile
 * Returns typical day profile with peak hours highlighted from historical EVI data
 * @param params.granularity - PT60 for hourly (24 points) or PT15 for quarter-hourly (96 points)
 * @param params.daysBack - Number of days of history to analyze (default: 365)
 */
export async function getEVITypicalDay(params?: {
  granularity?: Granularity;
  daysBack?: number;
}): Promise<EVITypicalDayResponse> {
  const searchParams = new URLSearchParams();
  if (params?.daysBack) searchParams.append("days_back", params.daysBack.toString());
  if (params?.granularity) searchParams.append("granularity", params.granularity);

  const query = searchParams.toString();
  return apiClient.get<EVITypicalDayResponse>(
    `/api/v1/evi/typical-day${query ? `?${query}` : ""}`
  );
}

/**
 * Get energy community statistics
 * Returns producer count, total capacity, and data date range
 */
export async function getEVICommunityStats(): Promise<EVICommunityStats> {
  return apiClient.get<EVICommunityStats>("/api/v1/evi/community-stats");
}

/**
 * Get autonomy stats for the energy community (Phase 15)
 * Returns degree of autonomy and self-consumption metrics
 */
export async function getEVIAutonomyStats(params?: {
  daysBack?: number;
}): Promise<EVIAutonomyStats> {
  const searchParams = new URLSearchParams();
  if (params?.daysBack) searchParams.append("days_back", params.daysBack.toString());

  const query = searchParams.toString();
  return apiClient.get<EVIAutonomyStats>(
    `/api/v1/evi/autonomy-stats${query ? `?${query}` : ""}`
  );
}

/**
 * Get typical consumption profile for the energy community (Phase 15)
 * Returns 24-hour average consumption profile
 */
export async function getEVIConsumptionProfile(params?: {
  daysBack?: number;
}): Promise<EVITypicalConsumptionProfile> {
  const searchParams = new URLSearchParams();
  if (params?.daysBack) searchParams.append("days_back", params.daysBack.toString());

  const query = searchParams.toString();
  return apiClient.get<EVITypicalConsumptionProfile>(
    `/api/v1/evi/consumption-profile${query ? `?${query}` : ""}`
  );
}

/**
 * Get production zones (peak/moderate/low hours) for the energy community (Phase 15)
 * Returns dynamic percentile-based production thresholds
 */
export async function getEVIProductionZones(params?: {
  daysBack?: number;
}): Promise<EVIProductionZones> {
  const searchParams = new URLSearchParams();
  if (params?.daysBack) searchParams.append("days_back", params.daysBack.toString());

  const query = searchParams.toString();
  return apiClient.get<EVIProductionZones>(
    `/api/v1/evi/production-zones${query ? `?${query}` : ""}`
  );
}

/**
 * Get combined typical day data (Phase 15)
 * Returns production, consumption, autonomy, and zones in one call
 */
export async function getEVICombinedTypicalDay(params?: {
  daysBack?: number;
}): Promise<EVICombinedTypicalDay> {
  const searchParams = new URLSearchParams();
  if (params?.daysBack) searchParams.append("days_back", params.daysBack.toString());

  const query = searchParams.toString();
  return apiClient.get<EVICombinedTypicalDay>(
    `/api/v1/evi/typical-day-combined${query ? `?${query}` : ""}`
  );
}

/**
 * @deprecated Use getEVITypicalDay instead - this uses the old ML forecast approach
 * Get energy community productive hours (old ML-based API)
 * Returns typical day profile with peak hours highlighted
 * @param params.granularity - PT60 for hourly (24 points) or PT15 for quarter-hourly (96 points)
 */
export async function getEVIForecast(params?: {
  plantId?: number;
  granularity?: Granularity;
}): Promise<EVIProfile> {
  const searchParams = new URLSearchParams();
  if (params?.plantId) searchParams.append("plant_id", params.plantId.toString());
  if (params?.granularity) searchParams.append("granularity", params.granularity);

  const query = searchParams.toString();
  return apiClient.get<EVIProfile>(
    `/api/v1/forecast/evi${query ? `?${query}` : ""}`
  );
}

/**
 * Get classification color for EVI data point
 */
export function getEVIColor(classification: "high" | "medium" | "low"): string {
  switch (classification) {
    case "high":
      return "#22c55e"; // green-500
    case "medium":
      return "#eab308"; // yellow-500
    case "low":
      return "#6b7280"; // gray-500
    default:
      return "#6b7280";
  }
}

/**
 * Get label for EVI classification
 */
export function getEVILabel(classification: "high" | "medium" | "low"): string {
  switch (classification) {
    case "high":
      return "Peak Production";
    case "medium":
      return "Good Production";
    case "low":
      return "Low Production";
    default:
      return "Unknown";
  }
}
